import React, {FocusEventHandler, useContext, useEffect, useState} from "react";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {IConstant} from "../dialogs/project/Constant";
import {ITag, Tags} from "../graph/StateTags";
import {importVariablesFromLockingServer} from "../../utility/cookbookImporter";
import {Button, List, ListItem, ListItemText, TextField} from "@mui/material";
import {PushId} from "../../utility/pushId";

const nodeTypeSynonyms: {[nodeType: string]: string[]} = {
    choice: ["CHOICE", "Choice", "Dialogue Choice"],
    consequence: ["CONSEQUENCE", "Consequence"],
    interaction: ["INTERACTION", "Interaction"],
    interactionDrama: ["INTERACTION DRAMA", "Interaction Drama"],
    dialog: ["NPC Dialogue"],
    passive: ["PASSIVE", "Passive", "Setting Info", "Image File"],
}

const interactiveNodeTypePattern = /^INT:\s*(\S+)/;

interface INode {
    m_components: string[]
    m_editableText0?: {
        m_contents: string
    }
    m_name: string
    m_template: string
    m_ulid: string
    m_ulidParent: string
}
interface ICookbookToolsProps {
    project: string
}
export default function CookbookTools({project}: ICookbookToolsProps) {
    const [importProject, setImportProject] = useState("");
    const [importTagsDisabled, setImportTagsDisabled] = useState(false);
    const [importVariablesDisabled, setImportVariablesDisabled] = useState(importProject === "");
    const {get, set} = useContext(FirebaseContext);
    const handleGuessNodeTypes = () => {
        setImportTagsDisabled(true);
        get(`constants/${project}`).then((constantsResult) => {
            const constants = constantsResult as {[key: string]: IConstant};
            let dialogueSources: {[source: string]: string[]} = {};
            let characterSynonyms: {[character: string]: string[]} = {};
            let nodeTypes: string[] = [];

            if (constants.character?.constraints) {
                const selectKey = Object.keys(constants.character.constraints).find(constraint =>
                    constants.character.constraints![constraint].type === "select");
                const select = selectKey && constants.character.constraints[selectKey];

                if (select) {
                    Object.keys(select.data.values).forEach(key => {
                        characterSynonyms[key] = [select.data.values[key], select.data.values[key].toUpperCase()];
                    });
                }
            }
            if (constants.dialogueSource?.constraints) {
                const selectKey = Object.keys(constants.dialogueSource.constraints).find(constraint =>
                    constants.dialogueSource.constraints![constraint].type === "select");
                const select = selectKey && constants.dialogueSource.constraints[selectKey];

                if (select) {
                    Object.keys(select.data.values).forEach(key => {
                        dialogueSources[key] = [select.data.values[key], select.data.values[key].toUpperCase()];
                    });
                }
            }
            if (constants.nodeType?.constraints) {
                const select = Object.keys(constants.nodeType.constraints).find(key =>
                    constants.nodeType.constraints![key].type === "select");
                
                if (select) {
                    nodeTypes = Object.keys(constants.nodeType.constraints![select].data.values);
                }
            }
            get(`tags/${project}`).then((tags) => {
                get(`nodes/${project}`).then(nodesResult => {
                    const rootNodes = nodesResult as { [key: string]: { [key: string]: any } };
                    const nodeKeys = Object.keys(rootNodes).filter(rootNodeKey => rootNodes[rootNodeKey]["nodes"]);
                    const templatesKey = Object.keys(rootNodes).find(rootNodeKey => rootNodes[rootNodeKey]["nodeStyles"]);
                    const templates: { [key: string]: ITag[] } = {};
                    const uniqueTypes = Object.keys(constants).filter(type =>
                        Object.values(constants[type].constraints || [])
                            .find(constraint => constraint.type === "unique"));
                    const selectTypes = Object.keys(constants).filter(type =>
                            Object.values(constants[type].constraints || [])
                            .find(constraint => constraint.type === "select"));
                    const filterUniqueTags = (tags: { [key: string]: ITag }) => Object.fromEntries(Object.entries(tags)
                        .filter(([key, tag], index, self) => self
                            .findIndex(([compareKey, compareTag], index, self) =>
                                compareKey === key ||
                                (compareTag.type === tag.type && uniqueTypes.includes(tag.type)) ||
                                (compareTag.type === tag.type && compareTag.value === tag.value)
                            ) === index));
                    const tagsUpdate = Object.fromEntries(Object.entries(tags as Tags || {})
                        .map(([key, stateTags]) => [key, filterUniqueTags(stateTags)]));
                    const missedTags = new Set();
                    const getTagsFromLabel = (label: string) => {
                        const nodeType = Object.keys(nodeTypeSynonyms).find(nodeType =>
                            nodeTypeSynonyms[nodeType].includes(label));
                        const character = Object.keys(characterSynonyms).find(character =>
                            characterSynonyms[character].includes(label));

                        if (nodeType) {
                            return [
                                {type: "nodeType", value: nodeType}
                            ];
                        } else if (character) {
                            return [
                                {type: "nodeType", value: "dialog"},
                                {type: "character", value: character},
                            ];
                        } else {
                            missedTags.add(label);
                            return [
                                {type: "nodeType", value: "passive"}
                            ];
                        }
                    }
                    const getTagsFromScript = (script: string, currentTags: ITag[]) => {
                        const dialogueSourcePattern = /^[A-Z\s0-9]+\((?<source>[^)]+)\):/g;
                        const wrylieSourcePattern = /(?<precursor>.*)\((?<wrylies>[^)]+)\)/g;
                        const imperativePattern = /---\s*\n(?<imperative>[A-Z0-9 .]*)\n/g;
                        const nodeType = currentTags.find(tag => tag.type === "nodeType" );
                        const tags: ITag[] = [];

                        for (let match of script.matchAll(dialogueSourcePattern)) {
                            if (match.groups) {
                                const {source} = match.groups;
                                const dialogueSource = Object.keys(dialogueSources).find(key =>
                                    dialogueSources[key].includes(source));

                                if (dialogueSource) {
                                    tags.push({type: "dialogueSource", value: dialogueSource});
                                }
                            }
                        }
                        for (let match of script.matchAll(wrylieSourcePattern)) {
                            if (match.groups) {
                                const {precursor, wrylies} = match.groups;

                                if (precursor === undefined || ! precursor.match(/(CONDITIONAL|STILL)/)) {
                                    wrylies.split(",").forEach(wrylie => {
                                        const trimmed = wrylie.trim();
                                        const dialogueSource = Object.keys(dialogueSources).find(key =>
                                            dialogueSources[key].includes(trimmed));

                                        if (!dialogueSource) {
                                            tags.push({type: "wrylies", value: trimmed});
                                        }
                                    });
                                }
                                break;
                            }
                        }
                        if (nodeType?.value === "interaction") {
                            const match = imperativePattern.exec(script);
                            
                            if (match && match.groups) {
                                tags.push({
                                    type: "imperative",
                                    value: match.groups.imperative
                                });
                            }
                        }
                        return tags;
                    };
                    const matchSelectTagValue = (tag: ITag)=>{
                        if (selectTypes.includes(tag.type))
                        {
                            const tagKey = String(tag.value).toLowerCase().replaceAll(" ", "");
                            const selectOptions = Object.keys(Object.values(constants[tag.type]?.constraints ?? {}).find((constraint) => constraint.type === "select")?.data?.values);
                            if (!selectOptions.includes(String(tag.value)))
                            {
                                for (const option of selectOptions)
                                {
                                    const optionKey = option.toLowerCase().replaceAll(" ", "");
                                    if (optionKey === tagKey)
                                    {
                                        tag.value = option;
                                        return;
                                    }
                                }
                                console.warn(`No match in select for ${tag.type} value: ${tag.value}`);
                            }
                        }
                    }
                    const addTag = (tag: ITag, state: string, tags: Tags) => {
                        matchSelectTagValue(tag);
                        if (!tags[state]) {
                            tags[state] = {};
                        }
                        if (!Object.keys(tags[state]).find(key =>
                            tags[state][key].type === tag.type && tags[state][key].value === tag.value
                        )) {
                            const uniqueKey = Object.keys(tags[state]).find(key =>
                                (tags[state][key].type === tag.type && uniqueTypes.includes(tag.type)));

                            if (uniqueKey) {
                                // Replace unique
                                tags[state][uniqueKey] = tag;
                            } else {
                                // Add non-unique
                                tags[state][PushId()] = tag;
                            }
                        }
                    };

                    if (templatesKey) {
                        const nodeStyles = rootNodes[templatesKey]["nodeStyles"] as { [key: string]: any }[];

                        nodeStyles.forEach(nodeStyle =>
                            templates[nodeStyle.m_template] = getTagsFromLabel(nodeStyle.m_name));
                    }
                    nodeKeys.forEach(nodeKey => {
                        rootNodes[nodeKey].nodes.forEach((node: INode) => {
                            if (templates[node.m_template]) {
                                templates[node.m_template].forEach(tag => addTag(tag, node.m_ulid, tagsUpdate));
                            } else if (node.m_components.length === 3 && node.m_components[2] === "Title Text" &&
                                node.m_editableText0?.m_contents) {
                                const match = node.m_editableText0!.m_contents.match(interactiveNodeTypePattern);

                                if (match) {
                                    addTag({type: "nodeType", value: "interaction"}, node.m_ulidParent, tagsUpdate);
                                    addTag({type: "interactionName", value: match[1]}, node.m_ulidParent, tagsUpdate);
                                } else {
                                    getTagsFromLabel(node.m_editableText0!.m_contents).forEach(tag =>
                                        addTag(tag, node.m_ulidParent, tagsUpdate));
                                }
                            }
                            if (node.m_components.length === 3 && node.m_components[2] === "Script Text" &&
                                node.m_editableText0?.m_contents) {
                                getTagsFromScript(node.m_editableText0!.m_contents,
                                    Object.values(tagsUpdate[node.m_ulidParent] || {})).forEach(tag =>
                                    addTag(tag, node.m_ulidParent, tagsUpdate));
                            }
                        });
                        if (nodeTypes.includes("composite")) {
                            addTag({type: "nodeType", value: "composite"}, nodeKey, tagsUpdate);
                        }
                    });
                    if (missedTags.size > 0) {
                        console.error("Failed to find node types for these tags: ");
                        console.error(missedTags);
                    }
                    if (Object.keys(tagsUpdate).length) {
                        set(`tags/${project}`, tagsUpdate).then(() => setImportTagsDisabled(false));
                    } else {
                        setImportTagsDisabled(false);
                    }
                });
            });
        });
    };
    const handleImportProjectBlur: FocusEventHandler<HTMLInputElement> = ({target}) =>
        setImportProject(target.value);
    const handleImportVariables = (event: React.MouseEvent<HTMLElement>) => {
        setImportVariablesDisabled(true);
        importVariablesFromLockingServer(importProject).then(registers => {
            set(`variables/${project}`, registers)
                .then(() => setImportVariablesDisabled(false))
                .catch(() => setImportVariablesDisabled(false));
        });
    }

    useEffect(() => {
        setImportVariablesDisabled(importProject === "");
    }, [importProject, setImportVariablesDisabled])
    return (
        <List>
            <ListItem>
                <ListItemText primary="Attempt to guess tags from node templates"/>
                <Button disabled={importTagsDisabled} variant="contained" onClick={handleGuessNodeTypes}>
                    Import
                </Button>
            </ListItem>
            <ListItem>
                <ListItemText primary="Attempt to import variables from locking server"/>
                <TextField
                    defaultValue={importProject} label="Locking Server Project Identifier"
                    onBlur={handleImportProjectBlur} size="small" sx={{marginLeft: 1}}/>
                <Button disabled={importVariablesDisabled} variant="contained" onClick={handleImportVariables} sx={{marginLeft: 1}}>
                    Import
                </Button>
            </ListItem>
        </List>
    )
}
